import { Box, Flex, Grid, GridItem } from '@chakra-ui/react';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Breadcrumbs, CustomHeading, Title } from 'shared-components';

import { getAuth, getStudent } from '../../../store/selectors';
import { hasPerm, isEnrolyStaff, P } from '../../../utilities/roles/permissions';
import { getBreadcrumbLinkItems, getDecryptedQuestions, getInterview } from './helpers';

import { getStudentRecordedInterviews } from '../../../store/student/actions';
import { requestScoresRegeneration, requestVideoProcessingStatus } from '../../../store/student/offer/actions';
import downloadStudentInterviewScoresCSV from '../../../utilities/downloadStudentIntInterviewScoresCSV';
import { getFacialRecognitionScore } from '../../../utilities/scheduledInterviews';
import RecordedInterviewAverageScoresSection from '../../molecules/RecordedInterviewAverageScoresSection';
import AssessSection from './components/AssessSection';
import DeletedInterview from './components/DeletedInterview';
import InterviewMenu from './components/InterviewMenu';
import InterviewVideoPlayer from './components/InterviewVideoPlayer';
import RecordedAt from './components/RecordedAt';
import { IRecordedInterviewEntry } from 'types';

export default function WatchInterview() {
  const dispatch = useDispatch();
  const { interviewHash } = useParams<{ interviewHash: string }>();
  const student = useSelector(getStudent);
  const { role } = useSelector(getAuth);
  const isAdmin = isEnrolyStaff(role);

  useEffect(() => {
    dispatch(getStudentRecordedInterviews());
  }, [dispatch]);

  const interview: IRecordedInterviewEntry | null = useMemo(() => {
    // @ts-expect-error - recordedInterviews is not in IOffer type but exists at runtime
    if (student.offer.recordedInterviews) {
      const currentInterview = getInterview(student, interviewHash ?? '');

      if (!currentInterview) {
        throw new Error('Interview was not found.');
      }
      return currentInterview;
    }
    return null;
  }, [interviewHash, student]);

  useEffect(() => {
    const videoId = interview?.videoId;
    if (interview && videoId && interviewHash) {
      dispatch(requestVideoProcessingStatus({ videoId, interviewHash }));
    }
  }, [dispatch, interview?.videoId, interviewHash]);

  if (!interview) {
    return null;
  }

  const { facialRecognitionScore, userAssessment } = interview;

  const canViewInterview = hasPerm(role, P.AI_INTERVIEW_ASSESSMENT_SCORES_VIEW);
  const revealAssessmentToUni = userAssessment?.revealAssessmentToUni ?? false;

  const canViewScoresAndAssessment = canViewInterview && revealAssessmentToUni;
  const canEditScoresAndAssessment = hasPerm(role, P.AI_INTERVIEW_ASSESSMENT_SCORES_EDIT);
  const showAssessment = canViewScoresAndAssessment || canEditScoresAndAssessment;

  const encryptedQuestions = interview.questions ?? '';
  const questionsCount = typeof encryptedQuestions === 'string' ? getDecryptedQuestions(encryptedQuestions).length : 0;

  const breadcrumbItems = getBreadcrumbLinkItems(student);

  const csvDownloadHandler = () => {
    const { scores } = interview;
    const { studentNo } = student.personalInfo;

    if (!scores || !studentNo) return;

    const data = scores.map(score =>
      Object.assign(score, { facialRecognitionScore: getFacialRecognitionScore(facialRecognitionScore) }),
    );
    downloadStudentInterviewScoresCSV(studentNo, data);
  };
  const regenereateScoresHandler = () => {
    const { videoId } = interview;

    if (!videoId) {
      toast.error(`videoId is absent for interview id: ${interview.interviewId}`);
      return;
    }

    dispatch(requestScoresRegeneration({ videoId }));
  };

  const pageTitle = `Watch ${student.personalInfo.givenName} ${student.personalInfo.surname} (${student.core.student_number}) ${interview.interviewName}`;

  return (
    <>
      <Title title={pageTitle} />

      {interview?.deletedByTTL ? (
        <DeletedInterview breadcrumbItems={breadcrumbItems} interview={interview} />
      ) : (
        <Box height={{ lg: '100vh' }} overflow={{ lg: 'hidden' }}>
          <Grid
            display={{ base: 'block', lg: 'grid' }}
            height="100%"
            maxWidth="100rem"
            mx="auto"
            templateColumns={{ lg: '35rem 1fr', xl: '40rem 1fr' }}
            templateRows={{ lg: '100%' }}
          >
            <GridItem
              w="100%"
              h="100%"
              overflowY={{ lg: 'auto' }}
              className="nav-scrollbar"
              px={{ base: 4, md: 6, xl: 8, '2xl': 12 }}
              py="6"
            >
              <Flex mb="4" alignItems="center">
                <Breadcrumbs items={breadcrumbItems} />

                {isAdmin && (
                  <Box ml="auto" my={-2}>
                    <InterviewMenu
                      onCSVDownload={csvDownloadHandler}
                      onRegenerateScores={regenereateScoresHandler}
                      // @ts-expect-error - processedVideos is not in IOffer type but exists at runtime
                      isProcessing={student.offer?.processedVideos !== questionsCount}
                    />
                  </Box>
                )}
              </Flex>

              <CustomHeading level="h1" mb={2}>
                {interview.interviewName}
              </CustomHeading>

              <RecordedAt
                interview={interview}
                // @ts-expect-error - processedVideos is not in IOffer type but exists at runtime
                processedVideos={student.offer?.processedVideos}
                questionsCount={questionsCount}
              />

              <InterviewVideoPlayer
                hashTimestamp={interview.hashTimestamp}
                hash={interview.hash}
                videoId={interview.videoId}
              />

              {showAssessment && <RecordedInterviewAverageScoresSection interview={interview} />}

              {showAssessment && (
                <AssessSection interview={interview} canEditScoresAndAssessment={canEditScoresAndAssessment} />
              )}
            </GridItem>

            <GridItem
              w="100%"
              h="100%"
              overflowY={{ lg: 'auto' }}
              className="nav-scrollbar"
              pl={4}
              pr={{ base: 4, lg: 6, xl: 8, '2xl': 12 }}
              py={4}
            >
              <Outlet context={{ interview, canEditScoresAndAssessment, canViewScoresAndAssessment }} />
            </GridItem>
          </Grid>
        </Box>
      )}
    </>
  );
}

export { default as Questions } from './components/Questions';
